import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { api, getToken, API } from "@/lib/api";
import { DEFAULT_COVER } from "@/lib/constants";

function coverUrl(book) {
  return book.cover_path ? `${API}/books/${book.id}/cover-img?auth=${getToken()}` : DEFAULT_COVER;
}

export default function SearchBar({ openBook }) {
  const [q, setQ] = useState("");
  const [books, setBooks] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const fetchBooks = () => {
    api.get("/books").then((r) => setBooks(r.data)).catch(() => {});
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const term = q.trim().toLowerCase();
  const results = term
    ? books
        .filter(
          (b) =>
            b.title?.toLowerCase().includes(term) ||
            b.author?.toLowerCase().includes(term) ||
            b.material_type?.toLowerCase().includes(term)
        )
        .slice(0, 8)
    : [];

  const choose = (b) => {
    openBook(b, b.last_page || 1);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative flex items-center border-l border-slate-200 dark:border-slate-800 px-2">
      <Search className="h-4 w-4 text-slate-400 pointer-events-none" />
      <input
        data-testid="search-input"
        value={q}
        placeholder="Search documents…"
        onFocus={() => { setOpen(true); fetchBooks(); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        className="w-40 sm:w-56 bg-transparent px-2 py-1 text-sm outline-none placeholder:text-slate-400 text-slate-700 dark:text-slate-200"
      />
      {q && (
        <button data-testid="search-clear" onClick={() => { setQ(""); setOpen(false); }} className="text-slate-400 hover:text-slate-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && term && (
        <div data-testid="search-results"
          className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto thin-scrollbar bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-1">
          {results.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No documents match "{q}".</p>
          ) : (
            results.map((b) => (
              <button key={b.id} data-testid="search-result"
                onClick={() => choose(b)}
                className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <img src={coverUrl(b)} alt="" className="h-10 w-7 object-cover rounded shrink-0"
                  onError={(e) => { e.currentTarget.src = DEFAULT_COVER; }} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate text-slate-900 dark:text-slate-100">{b.title}</p>
                  <p className="text-xs text-slate-500 truncate">{b.author || "Unknown"}{b.material_type ? ` · ${b.material_type}` : ""}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
